/**
 * OutcomePredictionWithConfidence - Combined outcome prediction and confidence display
 *
 * Atomic level: Molecule
 * Used in: RecordDetail Outcome Simulation
 *
 * Displays outcome prediction (Likely Upheld, Likely Not Upheld, Uncertain)
 * with color-coded styling and confidence percentage/bar
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../../lib/utils';
import type { OutcomePrediction } from '../../../../types/record';

const outcomeBadgeVariants = cva(
  'inline-flex items-center gap-2 rounded border font-medium',
  {
    variants: {
      prediction: {
        likely_upheld: 'bg-red-50 text-red-700 border-red-200',
        likely_not_upheld: 'bg-green-50 text-green-700 border-green-200',
        uncertain: 'bg-amber-50 text-amber-700 border-amber-200',
      },
      size: {
        sm: 'px-2 py-0.5 text-figma-xs',
        md: 'px-3 py-1.5 text-figma-sm',
        lg: 'px-3 py-1.5 text-figma-base',
      },
    },
    defaultVariants: {
      prediction: 'uncertain',
      size: 'md',
    },
  }
);

const barVariants = cva('h-1.5 rounded-full transition-all', {
  variants: {
    prediction: {
      likely_upheld: 'bg-red-500',
      likely_not_upheld: 'bg-green-500',
      uncertain: 'bg-amber-500',
    },
  },
  defaultVariants: {
    prediction: 'uncertain',
  },
});

interface OutcomePredictionWithConfidenceProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof outcomeBadgeVariants> {
  prediction: OutcomePrediction;
  confidence: number; // 0-100
  showBar?: boolean; // If true, show progress bar; if false, show percentage text
}

/**
 * Get human-readable label for outcome prediction
 */
const getOutcomeLabel = (prediction: OutcomePrediction): string => {
  switch (prediction) {
    case 'likely_upheld':
      return 'Likely Upheld';
    case 'likely_not_upheld':
      return 'Likely Not Upheld';
    case 'uncertain':
      return 'Uncertain';
  }
};

const OutcomePredictionWithConfidence = React.forwardRef<
  HTMLDivElement,
  OutcomePredictionWithConfidenceProps
>(({ className, prediction, confidence, size, showBar = false, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        outcomeBadgeVariants({ prediction, size }),
        showBar ? 'w-full sm:w-auto' : 'whitespace-nowrap',
        className
      )}
      {...props}
    >
      <span className="whitespace-nowrap">{getOutcomeLabel(prediction)}</span>
      {showBar ? (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="flex-1 h-2 bg-muted/50 rounded-full overflow-hidden">
            <div
              className={cn(barVariants({ prediction }), 'h-full')}
              style={{ width: `${confidence}%` }}
            />
          </div>
          <span className="text-xs font-semibold tabular-nums shrink-0">{confidence}%</span>
        </div>
      ) : (
        <span className="text-xs font-semibold tabular-nums">{confidence}%</span>
      )}
    </div>
  );
});

OutcomePredictionWithConfidence.displayName = 'OutcomePredictionWithConfidence';

export { OutcomePredictionWithConfidence, outcomeBadgeVariants };
